import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TasksDueResponse, TasksDueData, ChartData } from '../models/tasks-due.models';

@Injectable({
    providedIn: 'root'
})
export class TasksDueService {
    private readonly API_URL = `${environment.main_url}/agilelegal/reports/tasks/due-summary`;

    constructor(private http: HttpClient) { }

    getTasksDueData(): Observable<ChartData[]> {
        return this.http.get<TasksDueResponse>(this.API_URL).pipe(
            map(response => this.transformToChartData(response.data))
        );
    }

    private transformToChartData(data: any[]): any[] {
        // Define the order we want categories to appear in - chronological order
        const orderMap: Record<string, number> = {
            'Past Due': 1,                // Most urgent - already past due
            'Due <= 7 Days': 2,           // Due within a week
            'Due <= 15 Days': 3,          // Due within two weeks
            'Due <= 30 Days': 4,          // Due within a month
            'Due > 30 Days': 5           // Due in more than a month
        };

        // Filter out null values, ensure taskCount is a number, and exclude 'No Due Date' category
        const validData = data.filter(item => 
            item.dueCategory !== null && 
            item.taskCount !== null &&
            item.dueCategory !== 'No Due Date'
        );
        
        // Transform the data and sort by our defined order
        return validData
            .map(item => ({
                name: item.dueCategory,
                value: item.taskCount || 0,
                startDate: item.startDate || null,
                endDate: item.endDate || null
            }))
            .sort((a, b) => {
                // Use the orderMap for sorting, default to 999 if category not found
                const orderA = orderMap[a.name] || 999;
                const orderB = orderMap[b.name] || 999;
                return orderA - orderB;
            });
    }

    private getCategoryColor(category: string): string {
        switch (category) {
            case 'On Time': return 'green';
            case 'Due Date Approaching': return 'yellow';
            case 'At Risk': return 'orange';
            case 'Past Due Date': return 'red';
            default: return '#6c757d';
        }
    }
}
